import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { caregiverService } from '../../../services/caregiverService';
import { useAuth } from '../../../contexts/AuthContext';
import subscriptionService from '../../../services/subscriptionService';

const ProfileSelector = ({ selectedProfile, onSelectProfile }) => {
  const { userProfile } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planLimits, setPlanLimits] = useState(null);
  const [hasExistingProfiles, setHasExistingProfiles] = useState(false);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      
      // Get plan limits to determine if user can have managed profiles
      const limits = await subscriptionService?.getPlanLimits();
      setPlanLimits(limits);
      
      const maxManagedProfiles = limits?.managedProfiles || limits?.managed_profiles || 0;
      
      // Load managed profiles from database
      const { data, error } = await caregiverService?.getManagedProfiles();
      
      if (error) {
        console.error('Failed to load managed profiles:', error);
      }
      
      // Check if user has existing managed profiles (even if plan doesn't allow new ones)
      const existingManagedProfiles = data || [];
      setHasExistingProfiles(existingManagedProfiles?.length > 0);
      
      // Always include "Self" option
      const selfProfile = {
        id: 'self',
        name: `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`?.trim() || 'Myself',
        relationship: 'self',
        avatarUrl: userProfile?.avatar_url
      };
      
      const allProfiles = [
        selfProfile,
        ...existingManagedProfiles
      ];
      
      setProfiles(allProfiles);
      
      // CRITICAL FIX: Only auto-select if user has managed profiles AND no profile is selected
      // For Self users (no managed profiles), let selectedProfile remain null
      if (!selectedProfile && existingManagedProfiles?.length > 0) {
        // User has managed profiles - auto-select self
        onSelectProfile(selfProfile);
        console.log('🔄 Auto-selected self profile (user has managed profiles)');
      } else if (!selectedProfile && existingManagedProfiles?.length === 0) {
        // Self user with no managed profiles - do NOT auto-select, leave as null
        console.log('✅ Self user detected - leaving selectedProfile as NULL');
        // Don't call onSelectProfile - let it remain null
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-therapeutic p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Get max managed profiles from plan limits
  const maxManagedProfiles = planLimits?.managedProfiles || planLimits?.managed_profiles || 0;
  
  // CRITICAL FIX: Show selector if user has existing managed profiles OR if their plan allows it
  // This prevents data from becoming inaccessible after plan downgrades
  const shouldShowSelector = maxManagedProfiles > 0 || hasExistingProfiles;
  
  // ADDITIONAL SAFETY: Don't show selector if user has no managed profile capability AND no existing profiles
  // AND don't show if user only has "self" profile (no actual managed profiles)
  const managedProfilesOnly = profiles?.filter(p => p?.id !== 'self');
  
  if (!shouldShowSelector || managedProfilesOnly?.length === 0) {
    // User is a Self user with no managed profiles - don't show selector at all
    return null;
  }

  // Determine if user can add more profiles
  const canAddMore = maxManagedProfiles > managedProfilesOnly?.length;
  const isAtLimit = managedProfilesOnly?.length >= maxManagedProfiles && maxManagedProfiles > 0;
  const hasDowngradedPlan = hasExistingProfiles && maxManagedProfiles === 0;
  
  return (
    <div className="bg-card rounded-xl shadow-therapeutic p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Users" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-foreground">
          Who are you checking in for?
        </h3>
      </div>
      
      {/* Show info message based on user's situation */}
      {managedProfilesOnly?.length === 0 && maxManagedProfiles > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium mb-1">
                You can manage up to {maxManagedProfiles} {maxManagedProfiles === 1 ? 'dependent' : 'dependents'}
              </p>
              <p className="text-xs text-muted-foreground">
                Add family members or dependents in User Relationship Management to track their symptoms.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Show warning if user has downgraded but still has managed profiles */}
      {hasDowngradedPlan && (
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={18} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium mb-1">
                Limited Access: Plan Downgraded
              </p>
              <p className="text-xs text-muted-foreground">
                You can still view and track existing dependents, but your current plan doesn't allow adding new ones. Upgrade to Premium or Family to add more dependents.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Show limit warning if at capacity */}
      {isAtLimit && !hasDowngradedPlan && (
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={18} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium mb-1">
                Dependent Limit Reached
              </p>
              <p className="text-xs text-muted-foreground">
                You've reached your plan's limit of {maxManagedProfiles} {maxManagedProfiles === 1 ? 'dependent' : 'dependents'}. Upgrade to Family plan to manage up to 5 dependents.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {profiles?.map((profile) => (
          <button
            key={profile?.id}
            onClick={() => onSelectProfile(profile)}
            className={`
              relative p-4 rounded-lg border-2
              transition-all duration-300 ease-out
              transform hover:scale-[1.03] active:scale-[0.97]
              focus:outline-none focus:ring-2 focus:ring-primary/50
              ${
                selectedProfile?.id === profile?.id
                  ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20 ring-offset-1 ring-offset-background scale-[1.01]'
                  : 'border-border bg-background hover:border-primary/60 hover:shadow-md hover:bg-primary/[0.02]'
              }
            `}
          >
            <div className="flex items-center gap-3">
              <div className={`
                w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                transition-all duration-300
                ${selectedProfile?.id === profile?.id ? 'bg-primary/20 scale-110' : 'bg-primary/10'}
              `}>
                {profile?.avatarUrl ? (
                  <img
                    src={profile?.avatarUrl}
                    alt={profile?.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <Icon
                    name={profile?.id === 'self' ? 'User' : 'UserCircle'}
                    size={24}
                    color="var(--color-primary)"
                  />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className={`
                  font-medium text-sm transition-colors duration-200
                  ${selectedProfile?.id === profile?.id ? 'text-primary' : 'text-foreground'}
                `}>
                  {profile?.name}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {profile?.relationship?.replace('_', ' ')}
                </p>
              </div>
              <div className={`
                absolute top-2 right-2
                transition-all duration-300 ease-out
                ${selectedProfile?.id === profile?.id ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
              `}>
                <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
              </div>
            </div>
          </button>
        ))}
      </div>
      
      <p className="text-xs text-muted-foreground mt-4 text-center">
        {managedProfilesOnly?.length > 0 
          ? 'Select the person you\'re recording symptoms for'
          : 'Currently tracking for yourself only'}
      </p>
    </div>
  );
};

export default ProfileSelector;